import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

//hooks
import useFormatNumber from '@/hooks/format/useFormatNumber';

//components
import { CurrentPlanCard } from '@/components/composite/PricingCards/CurrentPlanCard';
import H2 from '@/components/base/Headings/H2';

//types
import type { ComponentPropsWithoutRef } from 'react';
import type { PlanOptionsType, PlanPriceType } from '@/types/pricingTypes/planDetailType';

//lib
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useUser from '@/utils/hooks/swr/useUser';
import { cardContentStyle, illustrationPlacementUserCard } from '@/lib/pricing/recapPlanCard';
import { trialPlanOptions } from '@/lib/PricingCards/trialPlanOptions';
import { SubscriptionType } from '@/types/pricingTypes/subscriptionType';

type UserPlanOptionsRecapProps = ComponentPropsWithoutRef<'div'>;

export function UserPlanCard({ ...restProps }: UserPlanOptionsRecapProps) {
    const { subscription } = useSubscription();
    const { t } = useTranslation('commons');
    const { user } = useUser();
    const { formatPriceSymbolNoSpace } = useFormatNumber(user?.settings?.numbers_format);
    let planOptions: PlanOptionsType;
    let planPrice: PlanPriceType;

    if (subscription.product_key === 'trial') {
        planOptions = trialPlanOptions;
        planPrice = {} as PlanPriceType; // for empty object as valid type since it's not needed for trial
    }
    if (subscription.product_key !== 'trial') {
        planOptions = {
            nb_social_profiles: subscription.options.profiles.count_total,
            nb_users: subscription.options.users.count_total
        };

        planPrice = {
            amount: subscription.total_monthly,
            currency: subscription.currency,
            frequency: subscription.frequency,
            notice: `pricing:billed_${subscription.frequency}_with_tax`
        };
    }
    return (
        <CurrentPlanCard
            variant={subscription.product_key}
            planOptions={planOptions}
            className="p-2"
            {...restProps}>
            <span
                className={clsx(
                    'z-0 flex',
                    illustrationPlacementUserCard[subscription.product_key]
                )}>
                <CurrentPlanCard.Illustration />
            </span>
            <div
                data-testid="current-plan-card-wrapper"
                className={clsx(
                    'relative flex min-h-[17rem] flex-col space-y-3 p-8 @[455px]:min-h-0'
                )}>
                <H2
                    className={clsx(
                        'flex items-baseline gap-1',
                        cardContentStyle[subscription.product_key]
                    )}>
                    <span>{t(`pricing.plans.${subscription.product_key}`)}</span>
                    <CurrentPlanCard.ApplePlan
                        shouldBeDisplayed={
                            'is_apple_subscription' in subscription &&
                            subscription.is_apple_subscription
                        }
                        size="medium"
                    />
                </H2>
                <UserPlanCardPrice
                    variant={subscription.product_key}
                    formatPriceSymbolNoSpace={formatPriceSymbolNoSpace}
                    planPrice={planPrice}
                />
            </div>
        </CurrentPlanCard>
    );
}

type UserPlanCardPriceProps = Readonly<{
    variant: SubscriptionType['product_key'];
    formatPriceSymbolNoSpace: ReturnType<typeof useFormatNumber>['formatPriceSymbolNoSpace'];
    planPrice: PlanPriceType;
}>;
function UserPlanCardPrice({
    variant,
    formatPriceSymbolNoSpace,
    planPrice
}: UserPlanCardPriceProps) {
    const { t } = useTranslation('commons');

    if (variant === 'trial') {
        return (
            <div className="font-GBold text-3xl" data-testid="trial-price-section">
                {t('pricing.on_trial')}
            </div>
        );
    }

    return (
        <div
            className="flex flex-col space-y-1 font-GBold"
            data-testid={`${variant}-price-section`}>
            <span className="flex items-baseline gap-x-0.5 text-xl">
                <span className="text-3.5xl">
                    {formatPriceSymbolNoSpace(planPrice.amount, planPrice.currency)}
                </span>
                /<span>{t('pricing.month')}</span>
            </span>
            <span className="font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                ({t(planPrice.notice)})
            </span>
        </div>
    );
}
